import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

/** Error envelope every non-2xx API response uses. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  get field(): string | undefined {
    const field = this.details.field;
    return typeof field === "string" ? field : undefined;
  }
}

let csrfToken: string | null = null;

/** The API hands the token out with the session; it must accompany every state change. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const csrf: Middleware = {
  onRequest({ request }) {
    if (csrfToken && !SAFE_METHODS.has(request.method)) {
      request.headers.set("X-CSRF-Token", csrfToken);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(csrf);

type Envelope = { error?: { code?: string; message?: string; details?: Record<string, unknown> } };

/** Unwraps an openapi-fetch result: returns data or throws a typed {@link ApiError}. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: Awaited<typeof call>;
  try {
    result = await call;
  } catch {
    throw new ApiError(0, "network_error", "Нет связи с сервером. Проверьте интернет");
  }
  const { data, error, response } = result;
  if (response.ok) {
    return data as T;
  }
  const envelope = (error ?? {}) as Envelope;
  throw new ApiError(
    response.status,
    envelope.error?.code ?? "unknown_error",
    envelope.error?.message ?? "Что-то пошло не так. Попробуйте ещё раз",
    envelope.error?.details ?? {},
  );
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}
